import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useRef,
  ReactNode,
  MutableRefObject,
} from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { authApi } from '../services/api';
import { User, LoginRequest, RegisterRequest } from '../types/auth';

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (credentials: LoginRequest) => Promise<User>;
  register: (userData: RegisterRequest) => Promise<User>;
  logout: () => void;
  loading: boolean;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

const TOKEN_STORAGE_KEY = 'token';
const LAST_ACTIVITY_STORAGE_KEY = 'auth:lastActivityAt';
const IDLE_LOGOUT_EVENT_STORAGE_KEY = 'auth:idleLogoutAt';
const IDLE_TIMEOUT_MS = 30 * 60 * 1000;
const ACTIVITY_SYNC_THROTTLE_MS = 5000;
const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart'] as const;

const getStoredToken = (): string | null => {
  try {
    const stored = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (stored && stored !== 'null' && stored !== 'undefined' && stored.trim() !== '') {
      return stored;
    }
  } catch (error) {
    console.error('Error reading token from localStorage:', error);
  }
  return null;
};

const getStoredTimestamp = (storageKey: string): number | null => {
  try {
    const stored = localStorage.getItem(storageKey);
    if (!stored) {
      return null;
    }

    const timestamp = Number(stored);
    return Number.isFinite(timestamp) && timestamp > 0 ? timestamp : null;
  } catch (error) {
    console.error(`Error reading ${storageKey} from localStorage:`, error);
    return null;
  }
};

const setStoredTimestamp = (storageKey: string, timestamp: number): boolean => {
  try {
    localStorage.setItem(storageKey, String(timestamp));
    return true;
  } catch (error) {
    console.error(`Error writing ${storageKey} to localStorage:`, error);
    return false;
  }
};

const clearStoredAuth = () => {
  try {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem(LAST_ACTIVITY_STORAGE_KEY);
  } catch (error) {
    console.error('Error clearing authentication data from localStorage:', error);
  }
};

const clearIdleTimer = (timerRef: MutableRefObject<number | null>) => {
  if (timerRef.current !== null) {
    window.clearTimeout(timerRef.current);
    timerRef.current = null;
  }
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { t } = useTranslation();
  // Use ref to track if we've initialized to prevent double initialization in StrictMode
  const initializedRef = useRef(false);
  const idleTimeoutRef = useRef<number | null>(null);
  const lastActivityWriteRef = useRef(0);
  const lastIdleLogoutEventRef = useRef<string | null>(null);

  // Initialize with token from localStorage immediately
  const initialToken = getStoredToken();
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(initialToken);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Prevent double initialization in React StrictMode
    if (initializedRef.current) {
      return;
    }
    initializedRef.current = true;

    const initializeAuth = async () => {
      const storedToken = getStoredToken();
      
      if (storedToken) {
        // Token exists, ensure it's in state
        setToken(storedToken);
        
        // Retry logic: try up to 2 times before giving up
        let retryCount = 0;
        const maxRetries = 2;
        
        while (retryCount < maxRetries) {
          try {
            const userData = await authApi.getCurrentUser();
            setUser(userData);
            // Ensure token is still set
            setToken(storedToken);
            break; // Success, exit retry loop
          } catch (error: any) {
            retryCount++;
            
            const status = error.response?.status;
            const isNetworkError = !error.response; // No response = network error
            const isServerError = status >= 500;
            const isAuthError = status === 401 || status === 403;
            
            if (isAuthError && retryCount >= maxRetries) {
              // Only clear token on confirmed authentication failures after retries
              clearStoredAuth();
              clearIdleTimer(idleTimeoutRef);
              setToken(null);
              setUser(null);
              break;
            } else if (isNetworkError || isServerError) {
              // Network/server error - wait and retry
              if (retryCount < maxRetries) {
                await new Promise(resolve => setTimeout(resolve, 500));
                continue;
              } else {
                // Max retries reached, but keep token (might be temporary issue)
                // Don't clear token - user might still be authenticated
                break;
              }
            } else if (retryCount >= maxRetries) {
              // Other error after max retries - keep token to be safe
              break;
            }
          }
        }
      } else {
        // No token found, ensure clean state
        setToken(null);
        setUser(null);
      }
      
      setLoading(false);
    };

    initializeAuth();
  }, []);

  useEffect(() => {
    const expireSessionForInactivity = (announce: boolean) => {
      clearIdleTimer(idleTimeoutRef);
      clearStoredAuth();

      const logoutEventId = String(Date.now());
      lastIdleLogoutEventRef.current = logoutEventId;
      setStoredTimestamp(IDLE_LOGOUT_EVENT_STORAGE_KEY, Number(logoutEventId));

      setToken(null);
      setUser(null);
      setLoading(false);

      if (announce) {
        toast.error(t('auth.sessionExpiredInactive'));
      }
    };

    const scheduleIdleLogout = (lastActivityAt: number) => {
      clearIdleTimer(idleTimeoutRef);

      const remainingMs = Math.max(IDLE_TIMEOUT_MS - (Date.now() - lastActivityAt), 0);
      idleTimeoutRef.current = window.setTimeout(() => {
        expireSessionForInactivity(true);
      }, remainingMs);
    };

    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === IDLE_LOGOUT_EVENT_STORAGE_KEY) {
        if (event.newValue && event.newValue !== lastIdleLogoutEventRef.current) {
          lastIdleLogoutEventRef.current = event.newValue;
          toast.error(t('auth.sessionExpiredInactive'));
        }
        return;
      }

      if (event.key === LAST_ACTIVITY_STORAGE_KEY) {
        const lastActivityAt = event.newValue ? Number(event.newValue) : null;
        if (lastActivityAt && Number.isFinite(lastActivityAt) && getStoredToken()) {
          lastActivityWriteRef.current = lastActivityAt;
          scheduleIdleLogout(lastActivityAt);
        }
        return;
      }

      if (event.key !== TOKEN_STORAGE_KEY) {
        return;
      }

      const storedToken = getStoredToken();
      setToken(storedToken);

      if (!storedToken) {
        clearIdleTimer(idleTimeoutRef);
        lastActivityWriteRef.current = 0;
        setUser(null);
        setLoading(false);
        return;
      }

      const lastActivityAt = getStoredTimestamp(LAST_ACTIVITY_STORAGE_KEY);
      if (lastActivityAt) {
        lastActivityWriteRef.current = lastActivityAt;
        scheduleIdleLogout(lastActivityAt);
      } else {
        const loginTimestamp = Date.now();
        setStoredTimestamp(LAST_ACTIVITY_STORAGE_KEY, loginTimestamp);
        lastActivityWriteRef.current = loginTimestamp;
        scheduleIdleLogout(loginTimestamp);
      }

      setLoading(true);
      authApi
        .getCurrentUser()
        .then((userData) => {
          setUser(userData);
        })
        .catch(() => {
          clearStoredAuth();
          clearIdleTimer(idleTimeoutRef);
          setToken(null);
          setUser(null);
        })
        .finally(() => {
          setLoading(false);
        });
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [t]);

  useEffect(() => {
    if (!token) {
      clearIdleTimer(idleTimeoutRef);
      lastActivityWriteRef.current = 0;
      return;
    }

    const expireSessionForInactivity = () => {
      clearIdleTimer(idleTimeoutRef);
      clearStoredAuth();

      const logoutEventId = String(Date.now());
      lastIdleLogoutEventRef.current = logoutEventId;
      setStoredTimestamp(IDLE_LOGOUT_EVENT_STORAGE_KEY, Number(logoutEventId));

      setToken(null);
      setUser(null);
      setLoading(false);
      toast.error(t('auth.sessionExpiredInactive'));
    };

    const scheduleIdleLogout = (lastActivityAt: number) => {
      clearIdleTimer(idleTimeoutRef);

      const remainingMs = Math.max(IDLE_TIMEOUT_MS - (Date.now() - lastActivityAt), 0);
      idleTimeoutRef.current = window.setTimeout(() => {
        expireSessionForInactivity();
      }, remainingMs);
    };

    const syncActivity = (timestamp: number, forceWrite: boolean) => {
      const shouldWrite =
        forceWrite || timestamp - lastActivityWriteRef.current >= ACTIVITY_SYNC_THROTTLE_MS;

      if (shouldWrite) {
        setStoredTimestamp(LAST_ACTIVITY_STORAGE_KEY, timestamp);
        lastActivityWriteRef.current = timestamp;
      }

      scheduleIdleLogout(timestamp);
    };

    const storedLastActivity = getStoredTimestamp(LAST_ACTIVITY_STORAGE_KEY);
    if (storedLastActivity && Date.now() - storedLastActivity >= IDLE_TIMEOUT_MS) {
      expireSessionForInactivity();
      return;
    }

    if (storedLastActivity) {
      lastActivityWriteRef.current = storedLastActivity;
      scheduleIdleLogout(storedLastActivity);
    } else {
      syncActivity(Date.now(), true);
    }

    const handleActivity = () => {
      syncActivity(Date.now(), false);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') {
        return;
      }

      const latestActivity = getStoredTimestamp(LAST_ACTIVITY_STORAGE_KEY);
      if (latestActivity && Date.now() - latestActivity >= IDLE_TIMEOUT_MS) {
        expireSessionForInactivity();
        return;
      }

      if (latestActivity) {
        lastActivityWriteRef.current = latestActivity;
        scheduleIdleLogout(latestActivity);
      } else {
        syncActivity(Date.now(), true);
      }
    };

    ACTIVITY_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, handleActivity, { passive: true });
    });
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      ACTIVITY_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, handleActivity);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearIdleTimer(idleTimeoutRef);
    };
  }, [token, t]);

  const login = async (credentials: LoginRequest) => {
    try {
      const response = await authApi.login(credentials);
      const { token: newToken, user: userData } = response;
      
      if (!newToken) {
        throw new Error('No token received from server');
      }
      
      // Store token securely
      try {
        localStorage.setItem(TOKEN_STORAGE_KEY, newToken);
      } catch (e) {
        console.error('Error storing token in localStorage:', e);
        throw new Error('Failed to store authentication token');
      }

      const loginTimestamp = Date.now();
      setStoredTimestamp(LAST_ACTIVITY_STORAGE_KEY, loginTimestamp);
      lastActivityWriteRef.current = loginTimestamp;
      
      setToken(newToken);
      setUser(userData);
      return userData;
    } catch (error) {
      // Clear any partial state on login failure
      setToken(null);
      setUser(null);
      throw error;
    }
  };

  const register = async (userData: RegisterRequest) => {
    try {
      const response = await authApi.register(userData);
      const { token: newToken, user: userInfo } = response;
      
      if (!newToken) {
        throw new Error('No token received from server');
      }
      
      // Store token securely
      try {
        localStorage.setItem(TOKEN_STORAGE_KEY, newToken);
      } catch (e) {
        console.error('Error storing token in localStorage:', e);
        throw new Error('Failed to store authentication token');
      }

      const loginTimestamp = Date.now();
      setStoredTimestamp(LAST_ACTIVITY_STORAGE_KEY, loginTimestamp);
      lastActivityWriteRef.current = loginTimestamp;
      
      setToken(newToken);
      setUser(userInfo);
      return userInfo;
    } catch (error) {
      // Clear any partial state on registration failure
      setToken(null);
      setUser(null);
      throw error;
    }
  };

  const logout = () => {
    clearIdleTimer(idleTimeoutRef);
    clearStoredAuth();
    lastActivityWriteRef.current = 0;
    setToken(null);
    setUser(null);
    setLoading(false);
  };

  // Determine authentication status
  // During loading, check localStorage directly to avoid race conditions
  // After loading, use state token
  const isAuthenticated = useMemo(() => {
    if (loading) {
      // While loading, check localStorage directly to ensure we don't redirect prematurely
      try {
        const stored = localStorage.getItem(TOKEN_STORAGE_KEY);
        return !!(stored && stored !== 'null' && stored !== 'undefined' && stored.trim() !== '');
      } catch {
        return false;
      }
    }
    // After loading, use state token
    return !!token && token !== 'null' && token !== 'undefined' && token.trim() !== '';
  }, [token, loading]);

  const value: AuthContextType = {
    user,
    token,
    login,
    register,
    logout,
    loading,
    isAuthenticated,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
